import React from 'react';
import { Phone, Mail, Instagram, MapPin } from 'lucide-react';
import SEO from '../components/SEO';

const Contact = () => {

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO 
        title="Contact Vidéaste Mariage Alsace | Devis Gratuit Strasbourg - Yastudio"
        description="Contactez Yastudio, vidéaste mariage en Alsace. Demandez votre devis gratuit pour votre film de mariage à Strasbourg, Mulhouse, Colmar. Tél: 06 03 09 42 98"
        keywords="contact vidéaste mariage Alsace, devis film mariage Strasbourg, vidéaste mariage Bas-Rhin, vidéaste mariage Haut-Rhin"
      />
      {/* Hero Section */}
      <section className="py-32 px-4 bg-gradient-to-b from-zinc-950 to-black">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            Contactez-moi
          </h1>
          <p className="text-xl md:text-2xl text-gray-300">
            Demande de devis personnalisé pour votre mariage
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-4 bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-8">
                Discutons de Votre Projet
              </h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                Vous recherchez un vidéaste professionnel pour immortaliser votre mariage, un 
                anniversaire, un baptême ou tout autre événement marquant ? Vous êtes au bon endroit.
              </p>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                Chaque événement est unique, et c'est pourquoi le tarif de ma prestation s'adapte 
                à vos besoins, à la durée de couverture souhaitée, au lieu, ainsi qu'au matériel 
                professionnel mobilisé.
              </p>

              <div className="space-y-6 mb-8">
                <a 
                  href="tel:0603094298"
                  className="flex items-start gap-4 hover:bg-zinc-900 p-4 rounded-lg transition-colors cursor-pointer"
                >
                  <div className="w-12 h-12 bg-light-pink rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Téléphone</h3>
                    <span className="text-gray-300 hover:text-light-pink transition-colors text-xl">
                      06 03 09 42 98
                    </span>
                  </div>
                </a>

                <a 
                  href="mailto:yastudio@outlook.fr"
                  className="flex items-start gap-4 hover:bg-zinc-900 p-4 rounded-lg transition-colors cursor-pointer"
                >
                  <div className="w-12 h-12 bg-mid-blue rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Email</h3>
                    <span className="text-gray-300 hover:text-mid-blue transition-colors text-xl">
                      yastudio@outlook.fr
                    </span>
                  </div>
                </a>

                <a 
                  href="https://www.instagram.com/yastudio_photographe_videaste/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 hover:bg-zinc-900 p-4 rounded-lg transition-colors cursor-pointer"
                >
                  <div className="w-12 h-12 bg-mid-purple rounded-full flex items-center justify-center flex-shrink-0">
                    <Instagram className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Instagram</h3>
                    <span className="text-gray-300 hover:text-mid-purple transition-colors text-xl">
                      @yastudio_photographe_videaste
                    </span>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-4">
                  <div className="w-12 h-12 bg-mid-yellow rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Zone d'intervention</h3>
                    <p className="text-gray-300 text-lg">
                      Basé en Alsace<br />
                      Déplacement dans toute la région Grand Est et au-delà
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-900 p-6 rounded-lg border border-zinc-800">
                <h3 className="font-semibold text-xl mb-3">⏱️ Réponse rapide garantie</h3>
                <p className="text-gray-300">
                  Je m'engage à vous répondre sous 24h pour discuter de votre projet et vous 
                  proposer un devis sur-mesure, clair et transparent.
                </p>
              </div>
            </div>

            {/* Photo Section */}
            <div>
              <div className="rounded-2xl overflow-hidden shadow-2xl h-full">
                <img 
                  src="https://customer-assets.emergentagent.com/job_alsace-wedding-pro/artifacts/mu6sw1rr_PHOTO-2024-10-24-16-44-00.jpeg"
                  alt="Yacine - Vidéaste professionnel en Alsace"
                  className="w-full h-full object-cover min-h-[600px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-4 bg-zinc-950">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ne Laissez Rien au Hasard
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Pour le plus beau jour de votre vie, confiez vos souvenirs à un vidéaste passionné, 
            humain et discret, qui saura capturer chaque émotion avec naturel et sensibilité.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:0603094298"
              className="inline-flex items-center justify-center bg-white text-black hover:bg-gray-200 rounded-full px-8 py-4 text-lg font-medium transition-colors"
            >
              <Phone className="w-5 h-5 mr-2" />
              Appelez Maintenant
            </a>
            <a 
              href="mailto:yastudio@outlook.fr"
              className="inline-flex items-center justify-center border-2 border-white text-white hover:bg-white hover:text-black rounded-full px-8 py-4 text-lg font-medium transition-colors"
            >
              <Mail className="w-5 h-5 mr-2" />
              Envoyez un Email
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;